import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { BannerNotificationsService } from '../../../../xform-compat';
import { Product, Reference, ReferenceType } from '../../../models';
import { AutocompleteService } from '../../../../core/data-services';
import { TextContentService } from '../../../../core/services';
import { Tag } from '../../../models/tag.model';
import { TagService } from '../../../../core/data-services/tag.service';
import { CONFIG } from '../../../../config';

/**
 * Component class for reference modal
 */
@Component({
    selector: 'rms-reference-modal',
    templateUrl: './reference-modal.component.html',
    styleUrls: ['./reference-modal.component.scss'],
    standalone: false
})
export class ReferenceModalComponent implements OnInit {
  private largePageSize = CONFIG.pagination.singleLargePage;

  @ViewChild('referenceModal', { static: true }) private referenceModalRef: ElementRef;
  @Input() public entity: string;
  @Input() public parentEntity: { id: number; type: string; };
  private selectedEntity: ReferenceType;
  private selectedNoteId: number;
  private selectedTextContentId: number;
  private modalReference: NgbModalRef;
  public referenceTypeahead = new Subject<string>();

  public companyRef: ReferenceType;
  public indicationRef: ReferenceType;
  public productRef: ReferenceType;
  public conferenceRef: ReferenceType;

  public allTags: Array<Tag>;
  public attachedTags: Array<Tag>;
  public modifiedTagIds: Array<number>;

  public isCrmNote = false;

  constructor(
    private modalService: NgbModal,
    private cd: ChangeDetectorRef,
    private autocompleteService: AutocompleteService,
    private textContentService: TextContentService,
    private tagService: TagService
  ) {
    this.setInitialState();
  }

  ngOnInit () {
    this.referenceAutocomplete();
  }

  private setInitialState(): void {
    this.companyRef = new ReferenceType({name: 'Company', type: 'companies', options: [], references: [], referencesIds: []});
    this.indicationRef = new ReferenceType({name: 'Indication', type: 'indications', options: [], references: [], referencesIds: []});
    this.productRef = new ReferenceType({name: 'Product', type: 'products', options: [], references: [], referencesIds: []});
    this.conferenceRef = new ReferenceType({name: 'Conference', type: 'conferences', options: [], references: [], referencesIds: []});
  }

  /**
   * Open references modal
   * @param references
   * @param noteId
   */
  public openModal (references: Array<Reference>, noteId?: number, textContentId?: number, isCrmNote?: boolean): void {
    this.isCrmNote = isCrmNote;
    this.setInitialState();
    this.initTags(textContentId || noteId);

    this.selectedTextContentId = textContentId || noteId;
    this.selectedNoteId = noteId ? noteId : references && references.length ? references[0].noteId :  null;

    if (references) {
      for (let i = 0; i < references.length; i++) {
        const ref = references[i];
        switch (ref.referencedType) {
          case 'COMPANY':
            this.companyRef.options.push(ref);
            this.companyRef.references.push(ref);
            this.companyRef.referencesIds.push(ref.referencedId);
            break;

          case 'INDICATION':
            this.indicationRef.options.push(ref);
            this.indicationRef.references.push(ref);
            this.indicationRef.referencesIds.push(ref.referencedId);
            break;

          case 'PRODUCT':
            this.productRef.options.push(ref);
            this.productRef.references.push(ref);
            this.productRef.referencesIds.push(ref.referencedId);
            break;

          case 'CONFERENCE':
            this.conferenceRef.options.push(ref);
            this.conferenceRef.references.push(ref);
            this.conferenceRef.referencesIds.push(ref.referencedId);
            break;
        }
      }
    }

    this.companyRef = { ...this.companyRef };
    this.indicationRef = { ...this.indicationRef };
    this.productRef = { ...this.productRef };
    this.conferenceRef = { ...this.conferenceRef };

    this.cd.markForCheck();

    this.modalReference = this.modalService.open(this.referenceModalRef, {size: 'lg'});
    this.modalReference.result.then(
      () => this.processForm(null, true),
      () => this.processForm(null, true)
    );
  }

  /**
   * Typeahead to search references
   */
  public referenceAutocomplete () {
    this.referenceTypeahead.pipe(
      distinctUntilChanged(),
      debounceTime(200),
      switchMap(term => this.autocompleteService.autocompleteEntity<any>(this.selectedEntity.type, { q: term })),
    ).subscribe(value => {
      value.records.map(item => {
        item.referencedId = item.id;
        item.referencedLabel = item.name;
      });

      switch (this.selectedEntity.name.toUpperCase()) {
        case 'COMPANY':
          this.companyRef.options =  this.filterRecords(value.records);
          break;
        case 'INDICATION':
          this.indicationRef.options = this.filterRecords(value.records);
          break;
        case 'PRODUCT':
          this.productRef.options = this.filterRecords(value.records);
          break;
        case 'CONFERENCE':
          this.conferenceRef.options = this.filterRecords(value.records);
          break;
      }

      this.cd.markForCheck();
    });
  }

  public filterRecords(arr) {
    if (this.parentEntity.type !== this.selectedEntity.name.toUpperCase()) {
      return arr;
    }
    return arr.filter( item => item.id !== `${this.parentEntity.id}`);

  }

  /**
   * On focus the reference value field, sets the entity value for the autocomplete service
   * @param referenceObj
   */
  public selectAutocompleteEntity (referenceObj: ReferenceType): void {
    this.selectedEntity = referenceObj;
  }

  public onAddRef(reference: any, referenceObj: ReferenceType): void {
    referenceObj.references = [...referenceObj.references, reference];
  }

  public onRemoveRef(removedItem: any, referenceObj: ReferenceType): void {
    referenceObj.references = referenceObj.references.filter((item) => item.referencedId !== removedItem.value.referencedId);
  }

  /**
   * Formats form data and emits to the parent component
   * Deletes no required form fields
   * Add to references the existing and new values
   * @param form
   * @param dontSaveReferences
   */
  public processForm (form: NgForm, dontSaveReferences?: boolean) {
    // TODO: validate if Form was touched or not

    if (!form && dontSaveReferences) {
      return;
    }

    const body = { references: [], noteId: this.selectedNoteId, isCrmNote: this.isCrmNote };
    body.references = body.references.concat(this.formatReference(this.companyRef));
    body.references = body.references.concat(this.formatReference(this.indicationRef));
    body.references = body.references.concat(this.formatReference(this.productRef));
    body.references = body.references.concat(this.formatReference(this.conferenceRef));

    this.textContentService.noteObjReferenceEdited(body);
    this.commitTagChanges();
  }

  public isTagAttached(id: number): boolean {
    return this.attachedTags && this.attachedTags.some(at => at.id === id);
  }

  public updateModifiedTags(evt: Tag) {
    if (this.modifiedTagIds.indexOf(evt.id) === -1) {
      this.modifiedTagIds = [...this.modifiedTagIds, evt.id];
    } else {
      this.modifiedTagIds = this.modifiedTagIds.filter(mti => mti !== evt.id);
    }
  }

  private formatReference(referenceGroup) {
    return referenceGroup.references.map( (item) => ({
        referencedType: referenceGroup.name.toUpperCase(),
        referencedLabel: item.referencedLabel,
        referencedId: item.referencedId || null
      }));
  }

  private initTags(noteId: number) {
    this.tagService.getTagsForTextContent(noteId, {pagesize: this.largePageSize})
      .subscribe(data => {
        this.attachedTags = data.records;
        this.modifiedTagIds = this.attachedTags.map(t => t.id);
      });
    this.tagService.getRecords({pagesize: this.largePageSize})
      .subscribe(allTags => {
        const { records } = allTags;
        records.sort((a: Tag, b: Tag) => a.text.localeCompare(b.text));
        this.allTags = records;
      });
  }

  private commitTagChanges() {
    const attachedTagIds = this.attachedTags.map(at => at.id);
    // remove tags
    const idsToRemove = attachedTagIds.filter(mti => !this.modifiedTagIds.includes(mti));
    // add tags
    const idsToAdd = this.modifiedTagIds.filter(mti => !attachedTagIds.includes(mti));
    idsToAdd.map(id => this.tagService.attachTagToNote(this.selectedTextContentId, id).subscribe());
    idsToRemove.map(id => this.tagService.detachTagFromNote(this.selectedTextContentId, id).subscribe());
  }
}
